import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../App';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AIProductsPage = () => {
  const { isDark } = useTheme();
  const appContext = useApp();
  const addToCart = appContext?.addToCart || (() => {
    alert('Please login to add items to cart');
  });
  const [aiProducts, setAIProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [customizationData, setCustomizationData] = useState({
    prompt: '',
    size: 'medium',
    material: 'pla',
    color: '#8B5CF6',
    uploadedImage: null,
    iterations: 0
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadAIProducts();
  }, []);

  useEffect(() => {
    if (showCustomizer) {
      // Lock body scroll and hide header
      document.body.style.overflow = 'hidden';
      const header = document.querySelector('header');
      if (header) {
        header.style.visibility = 'hidden';
      }
    } else {
      // Restore body scroll and header
      document.body.style.overflow = 'auto';
      const header = document.querySelector('header');
      if (header) {
        header.style.visibility = 'visible';
      }
    }
    
    // Cleanup
    return () => {
      document.body.style.overflow = 'auto';
      const header = document.querySelector('header');
      if (header) {
        header.style.visibility = 'visible';
      }
    };
  }, [showCustomizer]);

  const loadAIProducts = async () => {
    try {
      const response = await axios.get(`${API}/api/ai-products`);
      // Map backend response to frontend format
      const products = (response.data || []).map(product => ({
        id: product.id,
        name: product.name,
        description: product.description,
        basePrice: product.base_price,
        category: product.name.toLowerCase().replace(/ /g, '-'),
        image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="500" height="500"%3E%3Crect fill="%23667eea" width="500" height="500"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="48" text-anchor="middle" x="250" y="265"%3E3D Product%3C/text%3E%3C/svg%3E',
        icon: product.icon || '✨'
      }));
      setAIProducts(products);
    } catch (error) {
      console.error('Failed to load AI products:', error);
      setAIProducts(getPlaceholderProducts());
    } finally {
      setIsLoading(false);
    }
  };

  const getPlaceholderProducts = () => [
    {
      id: '1',
      name: 'Personalized Lithophane Ornaments',
      description: 'Custom 3D lithophane ornaments that you help design - transform your photos or ideas into beautiful illuminated keepsakes',
      basePrice: 15.00,
      category: 'ornaments',
      image: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="500" height="500"%3E%3Crect fill="%23667eea" width="500" height="500"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="48" text-anchor="middle" x="250" y="265"%3ELithophane%3C/text%3E%3C/svg%3E',
      icon: '✨'
    }
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomizationData({ ...customizationData, uploadedImage: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateDesign = async () => {
    setIsGenerating(true);
    try {
      // Call the actual backend API
      const response = await axios.post(`${API}/api/ai-products/generate-design`, {
        category_id: selectedCategory.id,
        prompt: customizationData.prompt,
        uploaded_image: null,  // TODO: Handle image upload later
        size: customizationData.size,
        material: customizationData.material,
        color: customizationData.color,
        iteration: customizationData.iterations
      });

      if (response.data.success) {
        setGeneratedPreview({
          imageUrl: response.data.preview_image_url || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="500" height="500"%3E%3Crect fill="%23764ba2" width="500" height="500"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="48" text-anchor="middle" x="250" y="250"%3EGenerated%3C/text%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="48" text-anchor="middle" x="250" y="300"%3EPreview%3C/text%3E%3C/svg%3E',
          description: response.data.design_description,
          price: response.data.estimated_price,
          specifications: response.data.specifications,
          materialRecommendations: response.data.material_recommendations,
          printTimeEstimate: response.data.print_time_estimate,
          modificationSuggestions: response.data.modification_suggestions,
          modelUsed: response.data.model_used
        });
        setCustomizationData({ ...customizationData, iterations: customizationData.iterations + 1 });
      } else {
        throw new Error('AI generation failed');
      }
    } catch (error) {
      console.error('Failed to generate design:', error);
      alert('Failed to generate design. Please try again or contact support.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddToCart = () => {
    if (!generatedPreview) return;
    
    // Create cart item from the AI-generated design
    const cartItem = {
      id: `ai-${Date.now()}`, // Unique ID for AI-generated product
      name: `${selectedCategory.name} - AI Custom Design`,
      description: generatedPreview.description.substring(0, 200) + '...',
      price: generatedPreview.price,
      quantity: 1,
      image: generatedPreview.imageUrl,
      isAIGenerated: true,
      specifications: {
        size: customizationData.size,
        material: customizationData.material,
        color: customizationData.color,
        prompt: customizationData.prompt,
        modelUsed: generatedPreview.modelUsed,
        iterations: customizationData.iterations
      }
    };
    
    addToCart(cartItem);
    alert('✅ AI-generated design added to cart! View your cart to proceed with checkout.');
    
    // Optionally close the modal
    // setShowCustomizer(false);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50'}`}>
      {/* Header Area */}
      <div className="relative overflow-hidden">
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-purple-900/20 to-blue-900/20' : 'bg-gradient-to-r from-purple-600/10 to-blue-600/10'}`} />
        
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className={`px-6 py-2 rounded-full text-sm font-semibold ${
                isDark 
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                  : 'bg-purple-100 text-purple-700 border border-purple-200'
              }`}>
                ✨ Powered by Advanced AI
              </span>
            </div>
            
            <h1 className={`text-5xl md:text-6xl font-black mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              AI-Generated{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
                Custom 3D Prints
              </span>
            </h1>
            
            <p className={`text-xl md:text-2xl max-w-3xl mx-auto mb-12 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Transform your ideas into reality with AI-powered design. Simply describe what you want, 
              and our advanced AI will generate a unique 3D printable design just for you.
            </p>

            {/* How It Works Section */}
            <div className={`max-w-5xl mx-auto p-8 rounded-2xl ${
              isDark ? 'bg-gray-800/50 border border-gray-700' : 'bg-white/80 border border-purple-100'
            } backdrop-blur-sm mb-12`}>
              <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                How It Works
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-3xl ${
                    isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                  }`}>
                    💭
                  </div>
                  <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>1. Describe</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Tell us what you want or upload a reference image
                  </p>
                </div>
                
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-3xl ${
                    isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                  }`}>
                    🤖
                  </div>
                  <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>2. AI Generates</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Our AI creates a unique design based on your input
                  </p>
                </div>
                
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-3xl ${
                    isDark ? 'bg-green-500/20' : 'bg-green-100'
                  }`}>
                    🎨
                  </div>
                  <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>3. Customize</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Refine size, color, material, and other details
                  </p>
                </div>
                
                <div className="text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-3xl ${
                    isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'
                  }`}>
                    🚀
                  </div>
                  <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>4. Order</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    We 3D print and deliver your unique creation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FEATURED: AI Lithophane Ornament */}
      <div id="featured-ornament" className="max-w-7xl mx-auto px-4 py-12">
        <div className={`rounded-3xl overflow-hidden shadow-2xl mb-16 ${
          isDark ? 'bg-gradient-to-br from-purple-900 to-blue-900' : 'bg-gradient-to-br from-purple-600 to-blue-600'
        }`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-8">
            <div className="text-white">
              <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
                ✨ NEW & FEATURED
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-4">
                AI Lithophane Ornament
              </h2>
              <p className="text-lg md:text-xl mb-6 opacity-90">
                Transform your photos into stunning 3D illuminated ornaments. Upload up to 3 images and optional text - our AI does the rest!
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">✓</span>
                  <span>Upload 1-3 photos + custom text</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">✓</span>
                  <span>AI-generated 3D preview in 1-2 minutes</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">✓</span>
                  <span>Beautiful glow when backlit</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/ai-products/lithophane-sphere')}
                  className="px-8 py-4 bg-white text-purple-600 font-bold text-lg rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex-1 sm:flex-initial"
                >
                  🌐 Create Your Ornament Now
                </button>
                <button
                  onClick={() => navigate('/ai-products/lithophane-sphere?designerRequest=true')}
                  className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex-1 sm:flex-initial border-2 border-white/30"
                >
                  🎨 Design For Me
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border-2 border-white/20">
                <div className="bg-gradient-to-br from-white/20 to-white/5 rounded-xl flex items-center justify-center relative overflow-hidden">
                  {/* Background Image - natural dimensions */}
                  <img 
                    src="https://customer-assets.emergentagent.com/job_smart-ornament-gen/artifacts/3g529r8a_ChatGPT%20Image%20Sep%2018%2C%202025%2C%2012_32_52%20PM.png"
                    alt="Lithophane Ornament"
                    className="w-full h-auto object-contain rounded-xl"
                  />
                  {/* Overlay gradient for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent rounded-xl"></div>
                  {/* Text Content on top */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white z-10">
                      <p className="text-xl font-semibold drop-shadow-lg">Starting at $15.00</p>
                      <p className="text-sm opacity-90 drop-shadow-lg">+ optional sizes</p>
                      <p className="text-sm opacity-80 drop-shadow-lg">+ optional add-ons</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* COMING SOON: AI Custom Creations */}
      <div id="coming-soon-creations" className="max-w-7xl mx-auto px-4 py-12">
        <div className={`rounded-3xl overflow-hidden shadow-2xl mb-16 relative ${
          isDark ? 'bg-gradient-to-br from-indigo-900 to-purple-900' : 'bg-gradient-to-br from-indigo-600 to-purple-600'
        }`}>
          {/* Coming Soon Ribbon */}
          <div className="absolute top-6 right-6 z-10">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 px-6 py-2 rounded-full font-bold text-sm shadow-lg transform rotate-3 animate-pulse">
              🚀 COMING SOON
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-8">
            <div className="text-white">
              <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
                🚀 COMING SOON
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-4">
                AI-Generated Custom Creations
              </h2>
              <p className="text-lg md:text-xl mb-6 opacity-90">
                Unlock endless possibilities with AI-powered personalization — crafted by AI with a little help from its human assistant (that's us!).
              </p>
              
              {/* Keychains & Name Tags */}
              <div className="mb-6 p-4 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <span>🔑</span> Keychains & Name Tags
                </h3>
                <p className="text-base opacity-90">
                  Personalized 3D designs — your name, your style, crafted by AI with a little help from its human assistant (that's us!).
                </p>
              </div>
              
              {/* Home Décor */}
              <div className="mb-6 p-4 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <span>🖼️</span> Home Décor Signs & Wall Art
                </h3>
                <p className="text-base opacity-90">
                  Turn words and quotes into stunning 3D wall pieces — imagined by AI, perfected by its favorite human partner, RG-3D.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  disabled
                  className="px-8 py-4 bg-white/40 text-white font-bold text-lg rounded-full cursor-not-allowed opacity-80 flex-1 sm:flex-initial"
                >
                  🔔 Notify Me When Available
                </button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border-2 border-white/20">
                <div className="bg-gradient-to-br from-white/20 to-white/5 rounded-xl flex items-center justify-center relative overflow-hidden min-h-[400px]">
                  {/* Placeholder with Coming Soon overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/60 to-purple-900/60 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <div className="text-center text-white z-10 p-8">
                      <div className="text-6xl mb-4 animate-bounce">🎨</div>
                      <p className="text-3xl font-black mb-2">Coming Soon!</p>
                      <p className="text-lg opacity-90">Exciting new AI products</p>
                      <p className="text-lg opacity-90">in development</p>
                    </div>
                  </div>
                  
                  {/* Background preview images (subtle) */}
                  <div className="absolute inset-0 grid grid-cols-2 gap-2 p-4 opacity-20">
                    <div className="bg-white/20 rounded-lg flex items-center justify-center text-4xl">
                      🔑
                    </div>
                    <div className="bg-white/20 rounded-lg flex items-center justify-center text-4xl">
                      🖼️
                    </div>
                    <div className="bg-white/20 rounded-lg flex items-center justify-center text-4xl">
                      🏷️
                    </div>
                    <div className="bg-white/20 rounded-lg flex items-center justify-center text-4xl">
                      🎨
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Customization Interface Modal */}
      {showCustomizer && (
        <div className="fixed inset-0 z-[10000] overflow-y-auto">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity z-[10000]" onClick={() => setShowCustomizer(false)}></div>
          <div className="flex items-start justify-center min-h-screen px-4 pt-4 pb-20 relative z-[10001]">
            
            <div className={`relative w-full max-w-6xl rounded-2xl shadow-2xl z-[10001] mt-4 ${
              isDark ? 'bg-gray-800' : 'bg-white'
            } overflow-hidden`}>
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <span>{selectedCategory?.icon}</span>
                    AI Design Studio - {selectedCategory?.name}
                  </h2>
                  <p className="text-purple-100 text-sm">Powered by Claude & ChatGPT</p>
                </div>
                <button 
                  onClick={() => setShowCustomizer(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[80vh] overflow-y-auto">
                {/* Left Side - Customization Controls */}
                <div className="space-y-6">
                  {/* Text Prompt */}
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Describe Your Design *
                    </label>
                    <textarea
                      value={customizationData.prompt}
                      onChange={(e) => setCustomizationData({ ...customizationData, prompt: e.target.value })}
                      placeholder="Example: A cute cartoon cat sitting on a crescent moon with stars around it..."
                      rows="4"
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Reference Image (Optional)
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                    }`}>
                      {customizationData.uploadedImage ? (
                        <div className="relative">
                          <img src={customizationData.uploadedImage} alt="Upload preview" className="max-h-40 mx-auto rounded" />
                          <button 
                            onClick={() => setCustomizationData({ ...customizationData, uploadedImage: null })}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <svg className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Click to upload or drag and drop
                          </p>
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Size Selector */}
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Size *
                    </label>
                    <select
                      value={customizationData.size}
                      onChange={(e) => setCustomizationData({ ...customizationData, size: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="small">Small (5cm) - +$0</option>
                      <option value="medium">Medium (10cm) - +$10</option>
                      <option value="large">Large (15cm) - +$20</option>
                      <option value="xlarge">X-Large (20cm) - +$35</option>
                    </select>
                  </div>

                  {/* Material Selector */}
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Material *
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'pla', label: 'PLA (Standard)', price: 0 },
                        { value: 'abs', label: 'ABS (Durable)', price: 5 },
                        { value: 'petg', label: 'PETG (Strong)', price: 8 },
                        { value: 'resin', label: 'Resin (Detailed)', price: 15 }
                      ].map((material) => (
                        <button
                          key={material.value}
                          onClick={() => setCustomizationData({ ...customizationData, material: material.value })}
                          className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                            customizationData.material === material.value
                              ? 'border-purple-600 bg-purple-600 text-white'
                              : isDark
                                ? 'border-gray-600 bg-gray-700 text-gray-300 hover:border-purple-500'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-purple-500'
                          }`}
                        >
                          <div className="text-sm">{material.label}</div>
                          <div className="text-xs opacity-75">+${material.price}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Picker */}
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Color *
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#000000', '#FFFFFF'].map((color) => (
                        <button
                          key={color}
                          onClick={() => setCustomizationData({ ...customizationData, color })}
                          className={`w-12 h-12 rounded-full border-4 transition-all ${
                            customizationData.color === color ? 'border-purple-600 scale-110' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Side - Preview Area */}
                <div className="space-y-6">
                  <div className={`rounded-xl p-6 border-2 ${
                    isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Preview Area
                    </h3>
                    
                    {isGenerating ? (
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="relative">
                          <div className="animate-spin rounded-full h-20 w-20 border-4 border-purple-600 border-t-transparent"></div>
                          <div className="absolute inset-0 flex items-center justify-center text-3xl">
                            🤖
                          </div>
                        </div>
                        <p className={`mt-4 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          AI is generating your design...
                        </p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          This may take 10-30 seconds
                        </p>
                      </div>
                    ) : generatedPreview ? (
                      <div>
                        <img 
                          src={generatedPreview.imageUrl} 
                          alt="Generated design" 
                          className="w-full h-80 object-cover rounded-lg mb-4"
                        />
                        <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            AI Design Description:
                          </h4>
                          <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {generatedPreview.description}
                          </p>
                          
                          {generatedPreview.modelUsed && (
                            <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              Generated by: {generatedPreview.modelUsed}
                            </p>
                          )}
                        </div>
                        
                        {generatedPreview.printTimeEstimate && (
                          <div className={`p-3 rounded-lg mb-4 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                            <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                              ⏱️ Estimated Print Time: {generatedPreview.printTimeEstimate}
                            </p>
                          </div>
                        )}
                        
                        <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Base Price:</span>
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>${selectedCategory?.basePrice}</span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Customizations:</span>
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>+${(generatedPreview.price - selectedCategory?.basePrice).toFixed(2)}</span>
                          </div>
                          <div className={`flex justify-between items-center pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total:</span>
                            <span className="text-2xl font-bold text-purple-600">${generatedPreview.price.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="text-6xl mb-4">🎨</div>
                        <p className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Your AI-generated design will appear here
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={handleGenerateDesign}
                      disabled={!customizationData.prompt || isGenerating}
                      className={`w-full px-6 py-4 rounded-full font-bold text-lg transition-all ${
                        !customizationData.prompt || isGenerating
                          ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                          : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:shadow-2xl hover:shadow-purple-500/50 transform hover:scale-105'
                      }`}
                    >
                      {isGenerating ? '⏳ Generating...' : generatedPreview ? '🔄 Refine Design' : '✨ Generate Design'}
                    </button>
                    
                    {generatedPreview && (
                      <button
                        onClick={handleAddToCart}
                        className="w-full px-6 py-4 bg-green-600 text-white rounded-full font-bold text-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Add to Cart - ${(generatedPreview.price + 15).toFixed(2)}
                      </button>
                    )}
                  </div>

                  {customizationData.iterations > 0 && (
                    <p className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      ✨ Design iteration #{customizationData.iterations}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Final CTA */}
      <div className={`py-20 ${isDark ? 'bg-gray-800/50' : 'bg-white/80'} backdrop-blur-sm`}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className={`text-4xl font-black mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ready to Create Your Unique Design?
          </h2>
          <p className={`text-xl mb-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Join thousands of happy customers who brought their ideas to life with AI
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('featured-ornament');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105"
          >
            🚀 Start Designing Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIProductsPage;
